import { Anchor, Box, Button, Checkbox, Divider, Group, Select, Stack, Text, Title } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import shopCategories from '../../constants/shopCategories';
import shopVelocities from '../../constants/shopVelocities';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';

const categoryOptions = shopCategories.map((c) => ({ label: c, value: c }));

const ActivateDomainTermsTab = ({ domain, account, handleChangeTab }) => {
  const fetch = useFetch();

  const [working, setWorking] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(!!domain.termsAcceptedAt);
  const [pricingAccepted, setPricingAccepted] = useState(!!domain.pricingAcceptedAt);
  const [category, setCategory] = useState(domain.category || '');
  const [velocity, setVelocity] = useState(domain.velocity || '');
  const [error, setError] = useState();
  const pricingUrl = account.partner?.pricingUrl || 'https://releva.ai/pricing/';

  const save = () => {
    setWorking(true);
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...domain,
        termsAcceptedAt: termsAccepted ? new Date() : undefined,
        pricingAcceptedAt: pricingAccepted ? new Date() : undefined,
        category,
        velocity,
      }),
    })
      .then(() => {
        setWorking(false);
        handleChangeTab({ name: 'terms', status: 'completed' });
      })
      .catch((e) => {
        setWorking(false);
        setError(e);
      });
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!domain || !account) {
    return null;
  }

  const incomplete = !pricingAccepted || !termsAccepted || !category || !velocity;

  return (
    <Stack>
      <Title order={3} ta="center">
        Welcome aboard!
      </Title>
      <Text ta="center" c="dimmed">
        Before we begin, tell us a bit about your shop and review the terms below.
      </Text>

      {/* The form is a narrow column centred in a wide card: full-width inputs
          under their own labels read as one flow, where the old right-aligned
          label column left the fields floating off-centre with nothing to line
          them up against. */}
      <Box maw={420} mx="auto" w="100%">
        <Stack gap="md">
          <Select
            label="Shop category"
            description="What you sell. It seeds the recommendations Releva generates for you."
            data={categoryOptions}
            value={category}
            onChange={setCategory}
            placeholder="Search categories..."
            // The placeholder has always invited typing; now it does something.
            searchable
            nothingFoundMessage="No matching category"
            required
          />
          <Select
            label="Product velocity"
            description="How quickly your catalog turns over."
            data={shopVelocities}
            value={velocity}
            onChange={setVelocity}
            placeholder="Select velocity..."
            searchable
            nothingFoundMessage="No matching option"
            required
          />

          <Divider my="xs" />

          <Stack gap="sm">
            <Checkbox
              name="termsAccepted"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.currentTarget.checked)}
              label={
                <>
                  I accept Releva&apos;s{' '}
                  <Anchor href="https://releva.ai/terms-of-use/" target="_blank" rel="noreferrer">
                    Terms of Use
                  </Anchor>
                </>
              }
            />
            <Checkbox
              name="pricingAccepted"
              checked={pricingAccepted}
              onChange={(e) => setPricingAccepted(e.currentTarget.checked)}
              label={
                <>
                  I accept Releva&apos;s{' '}
                  <Anchor href={pricingUrl} target="_blank" rel="noreferrer">
                    Pricing Policy
                  </Anchor>
                </>
              }
            />
          </Stack>
        </Stack>
      </Box>

      <Group justify="flex-end" mt="md">
        <Button
          disabled={incomplete || working}
          loading={working}
          onClick={save}
          rightSection={<IconChevronRight size={14} />}
        >
          Next
        </Button>
      </Group>
    </Stack>
  );
};

export default ActivateDomainTermsTab;
